import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { usePayVenue } from "./use-pay-venue";
import { resolvePayoutError } from "./resolve-payout-error";
import { formatMoney, moneyFromCentsString } from "./money";
import { PAY_REASON_MAX_LENGTH } from "./constants";
import type { PayOutcome, PayResponse } from "./types";
import type { VenueBalanceGroup } from "./group-balances-by-venue";

interface PayVenueDialogProps {
  readonly group: VenueBalanceGroup | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const OUTCOME_CLASS: Readonly<Record<PayOutcome, string>> = {
  PAID: "bg-emerald-500/10 text-emerald-600",
  CONNECT_INCOMPLETE: "bg-amber-500/10 text-amber-600",
  RACE_LOST: "bg-muted text-muted-foreground",
  FAILED: "bg-destructive/10 text-destructive",
};

export function PayVenueDialog({ group, open, onOpenChange }: PayVenueDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PayResponse | null>(null);
  const pay = usePayVenue();

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setResult(null);
    }
  }, [open]);

  if (!group) return null;

  const handleClose = (next: boolean) => {
    if (!next && pay.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    pay.mutate(
      { venueId: group.venueId, reason },
      {
        onSuccess: (res) => setResult(res),
        onError: (err) => setError(resolvePayoutError(err, t)),
      },
    );
  };

  const venueLabel = group.venueName ?? group.venueId;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="pay-venue-dialog">
        <DialogHeader>
          <DialogTitle>{t("finance.pay.title", { venue: venueLabel })}</DialogTitle>
          <DialogDescription>{t("finance.pay.description")}</DialogDescription>
        </DialogHeader>

        {result === null ? (
          <div className="space-y-4 py-4">
            {/* Pre-confirmation summary — this action moves real money via Stripe
                and is irreversible, so show exactly what will be paid per currency
                (and whether each is payable now) before the operator confirms. */}
            <div className="space-y-2">
              <p className="font-sans text-sm font-medium text-foreground">
                {t("finance.pay.summaryTitle")}
              </p>
              <ul data-testid="pay-summary" className="space-y-2">
                {group.balances.map((item) => {
                  const payable = item.hasConnect && item.payoutsEnabled;
                  return (
                    <li
                      key={item.currency}
                      data-testid={`pay-summary-${item.currency}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2"
                    >
                      <span className="font-mono text-sm text-foreground">
                        {formatMoney(moneyFromCentsString(item.totalPendingCents, item.currency))}{" "}
                        <span className="uppercase text-muted-foreground">{item.currency}</span>
                      </span>
                      <span
                        className={
                          payable
                            ? "inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-600"
                            : "inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600"
                        }
                      >
                        {payable
                          ? t("finance.report.status.payable")
                          : t("finance.report.status.blocked")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="pay-reason" className="font-sans text-sm font-medium text-foreground">
                {t("finance.pay.reasonLabel")}
              </label>
              <Textarea
                id="pay-reason"
                data-testid="pay-reason-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={PAY_REASON_MAX_LENGTH}
                rows={3}
                placeholder={t("finance.pay.reasonPlaceholder")}
                disabled={pay.isPending}
              />
              <p className="font-sans text-xs text-muted-foreground">
                {t("finance.pay.reasonHint")}
              </p>
            </div>

            {error && (
              <p
                data-testid="pay-error"
                role="alert"
                className="font-sans text-sm font-medium text-destructive"
              >
                {error}
              </p>
            )}
          </div>
        ) : (
          <div data-testid="pay-results" className="space-y-3 py-4">
            {result.results.length === 0 ? (
              <p
                data-testid="pay-nothing-pending"
                className="font-sans text-sm text-muted-foreground"
              >
                {t("finance.pay.nothingPending")}
              </p>
            ) : (
              <>
                <p className="font-sans text-sm font-medium text-foreground">
                  {t("finance.pay.resultsTitle")}
                </p>
                <ul className="space-y-2">
                  {result.results.map((row) => (
                    <li
                      key={row.currency}
                      data-testid={`pay-result-${row.currency}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 px-3 py-2"
                    >
                      <span className="font-mono text-sm text-foreground">
                        {formatMoney(moneyFromCentsString(row.amountCents, row.currency))}{" "}
                        <span className="uppercase text-muted-foreground">{row.currency}</span>
                      </span>
                      <span
                        data-testid={`pay-outcome-${row.currency}`}
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${OUTCOME_CLASS[row.outcome]}`}
                      >
                        {t(`finance.pay.outcome.${row.outcome}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {result === null ? (
            <>
              <Button
                data-testid="pay-cancel-button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={pay.isPending}
              >
                {t("finance.pay.cancel")}
              </Button>
              <Button
                data-testid="confirm-pay-button"
                onClick={handleSubmit}
                disabled={pay.isPending}
              >
                {pay.isPending ? t("finance.pay.pending") : t("finance.pay.confirm")}
              </Button>
            </>
          ) : (
            <Button data-testid="pay-done-button" onClick={() => handleClose(false)}>
              {t("finance.pay.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
